import type { ParentMember, TaskOccurrence } from "./types";

export type DueBucket = "OVERDUE" | "TODAY" | "UNDATED";

export interface TodayTask extends TaskOccurrence {
  dueBucket: DueBucket;
}

export interface TodayTaskGroup {
  key: "household" | string;
  label: string;
  member?: ParentMember;
  tasks: TodayTask[];
}

const MEMBER_ORDER = ["liyang", "zhiyu", "yunning", "ruining"] as const;
const DUE_BUCKET_ORDER: Record<DueBucket, number> = {
  OVERDUE: 0,
  TODAY: 1,
  UNDATED: 2,
};

export function groupOccurrencesForToday(
  occurrences: TaskOccurrence[],
  members: ParentMember[],
  today: string,
): TodayTaskGroup[] {
  const orderedMembers = [...members]
    .sort((left, right) => {
      const leftIndex = MEMBER_ORDER.indexOf(left.slug);
      const rightIndex = MEMBER_ORDER.indexOf(right.slug);
      return leftIndex - rightIndex;
    });

  const groups: TodayTaskGroup[] = [
    { key: "household", label: "全家", tasks: [] },
    ...orderedMembers.map((member) => ({
      key: member.memberId,
      label: member.displayName,
      member,
      tasks: [],
    })),
  ];
  const byKey = new Map(groups.map((group) => [group.key, group]));

  for (const occurrence of occurrences) {
    if (occurrence.status !== "OPEN") continue;
    const dueBucket = dueBucketFor(occurrence, today);
    if (!dueBucket) continue;

    const key =
      occurrence.assignment.type === "HOUSEHOLD"
        ? "household"
        : occurrence.assignment.memberId;
    byKey.get(key)?.tasks.push({ ...occurrence, dueBucket });
  }

  for (const group of groups) {
    group.tasks.sort((left, right) => {
      const bucketDifference =
        DUE_BUCKET_ORDER[left.dueBucket] - DUE_BUCKET_ORDER[right.dueBucket];
      if (bucketDifference !== 0) return bucketDifference;
      const dateDifference = (left.scheduledDate ?? "").localeCompare(
        right.scheduledDate ?? "",
      );
      if (dateDifference !== 0) return dateDifference;
      if (left.sortOrder !== right.sortOrder) return left.sortOrder - right.sortOrder;
      return left.occurrenceId.localeCompare(right.occurrenceId);
    });
  }

  return groups;
}

function dueBucketFor(
  occurrence: TaskOccurrence,
  today: string,
): DueBucket | null {
  if (occurrence.scheduledDate === null) return "UNDATED";
  if (occurrence.scheduledDate < today) return "OVERDUE";
  if (occurrence.scheduledDate === today) return "TODAY";
  return null;
}
